import { useState, useCallback, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { Button, Card, ThemeToggle } from '../../../components/ui';
import {
  QuestionCard,
  QuestionNavigation,
  MultipleChoice,
  ShortAnswer,
  EssayResponse,
  ImageUpload,
  TableCompletion,
} from '../../../components/questions';

export default function PracticeQuestions({
  student,
  session,
  questions,
  responses: initialResponses = {},
  current_question_index = 0,
}) {
  const { t } = useTranslation();
  const [questionIndex, setQuestionIndex] = useState(current_question_index);
  const [responses, setResponses] = useState(() => {
    const initial = {};
    questions.forEach((q) => {
      initial[q.id] = initialResponses[q.id] || q.current_response || '';
    });
    return initial;
  });
  const [uploadedImages, setUploadedImages] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[questionIndex];
  const totalQuestions = questions.length;

  // Track current question for teacher's live session map
  const lastTrackedQuestionRef = useRef(null);
  useEffect(() => {
    if (!currentQuestion) return;

    // Only track if question changed
    if (lastTrackedQuestionRef.current === currentQuestion.id) return;
    lastTrackedQuestionRef.current = currentQuestion.id;

    const trackQuestion = async () => {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        await fetch(`/student/practice/${session.id}/track_question`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ question_id: currentQuestion.id }),
        });
      } catch (error) {
        // Silently ignore tracking errors - they shouldn't block the student
        console.debug('Question tracking failed:', error);
      }
    };

    trackQuestion();
  }, [currentQuestion?.id, session.id]);

  const handleResponseChange = useCallback((questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const handleImageUpload = useCallback((questionId, imageData) => {
    setUploadedImages((prev) => ({
      ...prev,
      [questionId]: imageData.previewUrl,
    }));
  }, []);

  const handleRemoveImage = useCallback((questionId) => {
    setUploadedImages((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  }, []);

  const handlePreviousQuestion = useCallback(() => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    }
  }, [questionIndex]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    // Debug: log what we're sending
    console.log('[Submit] Responses state:', responses);
    console.log('[Submit] JSON payload:', JSON.stringify({ responses }));

    try {
      // Submit all responses to the server
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/student/practice/${session.id}/submit_responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ responses }),
      });

      if (response.ok) {
        const data = await response.json();
        router.visit(data.redirect_to || `/student/practice/${session.id}/feedback`);
      } else {
        console.error('Failed to submit responses');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting responses:', error);
      setIsSubmitting(false);
    }
  }, [session.id, responses]);

  const handleNextQuestion = useCallback(() => {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      handleSubmit();
    }
  }, [questionIndex, totalQuestions, handleSubmit]);

  const handleSaveAndExit = useCallback(() => {
    router.visit('/');
  }, []);

  const handleBackToReading = useCallback(() => {
    router.visit(`/student/practice/${session.id}/read`);
  }, [session.id]);

  const canProceedToNext = () => {
    if (!currentQuestion) return false;
    const response = responses[currentQuestion.id];
    if (!response) return false;

    // Handle string responses (short_answer, essay, multiple_choice)
    if (typeof response === 'string') {
      return response.trim().length > 0;
    }

    // Handle object responses (table_completion)
    if (typeof response === 'object' && response.placements) {
      return Object.keys(response.placements).length > 0;
    }

    return false;
  };

  const answeredCount = questions.filter((q) => {
    const response = responses[q.id];
    if (!response) return false;
    if (typeof response === 'string') return response.trim().length > 0;
    if (typeof response === 'object' && response.placements) {
      return Object.keys(response.placements).length > 0;
    }
    return false;
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                {t('practice.step2of3')}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('practice.questions')}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                ({answeredCount}/{totalQuestions} {t('practice.answered')})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleBackToReading}>
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                {t('practice.backToReading')}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSaveAndExit}>
                {t('practice.saveAndExit')}
              </Button>
              <ThemeToggle />
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full w-2/3 bg-gray-900 dark:bg-white rounded-full transition-all duration-500" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Empty state — no questions on this activity */}
        {totalQuestions === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
              {t('practice.noQuestionsAvailable', 'No questions available for this activity.')}
            </p>
            <Button
              variant="ghost"
              onClick={handleSaveAndExit}
              className="mt-6 text-sm text-gray-500 dark:text-gray-400 underline underline-offset-2 hover:bg-transparent"
            >
              {t('practice.returnToDashboard', 'Return to dashboard')}
            </Button>
          </div>
        )}

        {/* Question Card */}
        {currentQuestion && (
          <div className="space-y-6">
            <QuestionCard
              questionNumber={questionIndex + 1}
              totalQuestions={totalQuestions}
              prompt={currentQuestion.prompt}
              guidance={currentQuestion.guidance}
              questionType={currentQuestion.question_type}
            >
              {currentQuestion.question_type === 'multiple_choice' && (
                <MultipleChoice
                  name={`question-${currentQuestion.id}`}
                  options={currentQuestion.options || []}
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                />
              )}

              {currentQuestion.question_type === 'short_answer' && (
                <ShortAnswer
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                  placeholder={t('practice.typeAnswerHere')}
                />
              )}

              {currentQuestion.question_type === 'essay' && (
                <EssayResponse
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                  placeholder={t('practice.writeResponseHere')}
                  minWords={100}
                  maxWords={500}
                />
              )}

              {currentQuestion.question_type === 'table_completion' && (
                <TableCompletion
                  options={currentQuestion.options}
                  value={responses[currentQuestion.id]}
                  onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                />
              )}
            </QuestionCard>

            {/* Navigation */}
            <QuestionNavigation
              currentIndex={questionIndex}
              totalQuestions={totalQuestions}
              onPrevious={handlePreviousQuestion}
              onNext={handleNextQuestion}
              canGoNext={canProceedToNext()}
              nextLabel={questionIndex === totalQuestions - 1 ? t('common.submit') : undefined}
            />

            {/* Image upload for handwritten work */}
            <Card className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" padding="sm">
              <div className="flex items-start gap-3">
                <div className="text-gray-400 dark:text-gray-500">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {t('practice.preferHandwriting')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('practice.uploadHandwritten')}
                  </p>
                  <div className="mt-3">
                    <ImageUpload
                      value={uploadedImages[currentQuestion.id]}
                      onUpload={(data) => handleImageUpload(currentQuestion.id, data)}
                      onRemove={() => handleRemoveImage(currentQuestion.id)}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Submitting overlay */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg shadow-lg">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('practice.submittingResponses')}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
